import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import brand from "../config/brand";

// LeaseExecution.jsx — S10
// AS-118: leases collection
// AS-122: Typed-name e-sign, scroll gate, E-SIGN/UETA compliant
// AS-128: PM creates draft lease → tenant signs via /lease?l=LEASE_ID public route
// This file handles BOTH the PM lease management view AND the public tenant signing view
// Public signing view is exported separately as LeaseSigningPortal (used in App.js /lease route)

const LEASE_STATUS_COLORS = {
  draft:      { bg: "#f5f5f5",  color: "#888888" },
  active:     { bg: "#f0fdf4",  color: "#16a34a" },
  expired:    { bg: "#fffbeb",  color: "#d97706" },
  terminated: { bg: "#fef2f2",  color: "#dc2626" },
};

function LeaseBadge({ status }) {
  const c = LEASE_STATUS_COLORS[status] || LEASE_STATUS_COLORS.draft;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 10,
      background: c.bg, color: c.color, textTransform: "capitalize",
    }}>
      {status}
    </span>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={pmStyles.infoRow}>
      <span style={pmStyles.infoLabel}>{label}</span>
      <span style={{ ...pmStyles.infoValue, ...(mono ? { fontFamily: "monospace", fontSize: 12 } : {}) }}>
        {value || "—"}
      </span>
    </div>
  );
}

// ── PM Lease Management View ──────────────────────────────────────────────
export default function LeaseExecution({ clientId }) {
  const db = getFirestore();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLease, setSelectedLease] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [form, setForm] = useState({
    tenantName: "", tenantEmail: "", tenantPhone: "",
    propertyId: "", unitId: "",
    startDate: "", endDate: "",
    monthlyRentCents: "", securityDepositCents: "",
    lateFeeCents: "", lateFeeGraceDays: "5",
    petPolicy: "", specialTerms: "",
  });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!clientId) return;
    const q = query(collection(db, "leases"), where("clientId", "==", clientId));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setLeases(data);
      setLoading(false);
    });
    return () => unsub();
  }, [clientId, db]);

  const updateForm = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  async function handleCreateLease() {
    setFormError("");
    if (!form.tenantName.trim()) { setFormError("Tenant name is required."); return; }
    if (!form.tenantEmail.trim()) { setFormError("Tenant email is required."); return; }
    if (!form.startDate || !form.endDate) { setFormError("Start and end dates are required."); return; }
    if (!form.monthlyRentCents) { setFormError("Monthly rent is required."); return; }

    setSaving(true);
    try {
      const leaseRef = await addDoc(collection(db, "leases"), {
        clientId,
        tenantName:           form.tenantName.trim(),
        tenantEmail:          form.tenantEmail.trim().toLowerCase(),
        tenantPhone:          form.tenantPhone.trim() || null,
        propertyId:           form.propertyId.trim() || null,
        unitId:               form.unitId.trim() || null,
        startDate:            form.startDate,
        endDate:              form.endDate,
        monthlyRentCents:     parseInt(form.monthlyRentCents) * 100,
        securityDepositCents: form.securityDepositCents ? parseInt(form.securityDepositCents) * 100 : null,
        lateFeeCents:         form.lateFeeCents ? parseInt(form.lateFeeCents) * 100 : null,
        lateFeeGraceDays:     parseInt(form.lateFeeGraceDays) || 5,
        petPolicy:            form.petPolicy.trim() || null,
        specialTerms:         form.specialTerms.trim() || null,
        status:               "draft",
        signedAt:             null,
        signedName:           null,
        createdAt:            serverTimestamp(),
        updatedAt:            serverTimestamp(),
      });

      setShowForm(false);
      setForm({
        tenantName: "", tenantEmail: "", tenantPhone: "",
        propertyId: "", unitId: "", startDate: "", endDate: "",
        monthlyRentCents: "", securityDepositCents: "",
        lateFeeCents: "", lateFeeGraceDays: "5",
        petPolicy: "", specialTerms: "",
      });
    } catch (err) {
      setFormError("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function copySigningLink(leaseId) {
    navigator.clipboard.writeText(`${window.location.origin}/lease?l=${leaseId}`);
    setCopiedId(leaseId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const fmtMoney = (cents) => cents
    ? (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";
  const fmtDate = (s) => s
    ? new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  return (
    <div style={pmStyles.wrapper}>
      <div style={pmStyles.header}>
        <div>
          <h2 style={pmStyles.heading}>Lease Execution</h2>
          <p style={pmStyles.subheading}>
            {leases.filter((l) => l.status === "active").length} active ·{" "}
            {leases.filter((l) => l.status === "draft").length} awaiting signature
          </p>
        </div>
        <button style={pmStyles.btnNew} onClick={() => setShowForm(true)}>+ New Lease</button>
      </div>

      {loading ? (
        <div style={pmStyles.loadingWrap}><div style={pmStyles.spinner} /></div>
      ) : leases.length === 0 ? (
        <div style={pmStyles.emptyState}>
          <p style={pmStyles.emptyTitle}>No leases yet</p>
          <p style={pmStyles.emptySub}>Create a lease draft and share the signing link with your tenant.</p>
          <button style={pmStyles.btnNew} onClick={() => setShowForm(true)}>+ New Lease</button>
        </div>
      ) : (
        <div style={pmStyles.list}>
          {leases.map((lease) => (
            <div key={lease.id} style={pmStyles.leaseRow} onClick={() => setSelectedLease(lease)}>
              <div style={pmStyles.leaseMain}>
                <div style={pmStyles.leaseTenant}>{lease.tenantName}</div>
                <div style={pmStyles.leaseMeta}>
                  {fmtDate(lease.startDate)} – {fmtDate(lease.endDate)}
                  {" · "}
                  {fmtMoney(lease.monthlyRentCents)}/mo
                </div>
              </div>
              <LeaseBadge status={lease.status} />
              {lease.status === "draft" && (
                <button
                  style={pmStyles.copyBtn}
                  onClick={(e) => { e.stopPropagation(); copySigningLink(lease.id); }}
                >
                  {copiedId === lease.id ? "✓ Copied" : "Copy Link"}
                </button>
              )}
              <span style={pmStyles.arrowLink}>→</span>
            </div>
          ))}
        </div>
      )}

      {/* Lease Detail Drawer */}
      {selectedLease && (
        <div style={pmStyles.overlay} onClick={() => setSelectedLease(null)}>
          <div style={{ ...pmStyles.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={pmStyles.modalHeader}>
              <h3 style={pmStyles.modalTitle}>Lease Detail</h3>
              <button style={pmStyles.closeBtn} onClick={() => setSelectedLease(null)}>✕</button>
            </div>
            <div style={pmStyles.modalBody}>
              <InfoRow label="Tenant"      value={selectedLease.tenantName} />
              <InfoRow label="Email"       value={selectedLease.tenantEmail} />
              <InfoRow label="Phone"       value={selectedLease.tenantPhone} />
              <InfoRow label="Start Date"  value={fmtDate(selectedLease.startDate)} />
              <InfoRow label="End Date"    value={fmtDate(selectedLease.endDate)} />
              <InfoRow label="Monthly Rent" value={fmtMoney(selectedLease.monthlyRentCents)} />
              <InfoRow label="Security Deposit" value={fmtMoney(selectedLease.securityDepositCents)} />
              <InfoRow label="Late Fee"    value={fmtMoney(selectedLease.lateFeeCents)} />
              <InfoRow label="Grace Days"  value={selectedLease.lateFeeGraceDays ? `${selectedLease.lateFeeGraceDays} days` : "—"} />
              <InfoRow label="Status"      value={selectedLease.status} />
              <InfoRow label="Signed At"   value={selectedLease.signedAt?.toDate
                ? selectedLease.signedAt.toDate().toLocaleDateString("en-US")
                : selectedLease.signedAt ? "Signed" : "Not yet signed"} />
              {selectedLease.signedName && <InfoRow label="Signed Name" value={selectedLease.signedName} />}
              {selectedLease.specialTerms && <InfoRow label="Special Terms" value={selectedLease.specialTerms} />}
              <InfoRow label="Lease ID" value={selectedLease.id} mono />

              {selectedLease.status === "draft" && (
                <div style={pmStyles.signingLinkBox}>
                  <span style={pmStyles.signingLinkLabel}>Tenant Signing Link</span>
                  <div style={pmStyles.signingLinkRow}>
                    <span style={pmStyles.signingLink}>
                      {window.location.origin}/lease?l={selectedLease.id}
                    </span>
                    <button
                      style={pmStyles.btnSmallPrimary}
                      onClick={() => copySigningLink(selectedLease.id)}
                    >
                      {copiedId === selectedLease.id ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Lease Modal */}
      {showForm && (
        <div style={pmStyles.overlay} onClick={() => setShowForm(false)}>
          <div style={pmStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={pmStyles.modalHeader}>
              <h3 style={pmStyles.modalTitle}>Create New Lease</h3>
              <button style={pmStyles.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={pmStyles.modalBody}>
              <div style={pmStyles.formRow}>
                <FormField label="Tenant Name *">
                  <input style={pmStyles.input} value={form.tenantName}
                    onChange={(e) => updateForm("tenantName", e.target.value)} placeholder="Jane Smith" />
                </FormField>
                <FormField label="Tenant Email *">
                  <input style={pmStyles.input} type="email" value={form.tenantEmail}
                    onChange={(e) => updateForm("tenantEmail", e.target.value)} placeholder="jane@email.com" />
                </FormField>
              </div>
              <div style={pmStyles.formRow}>
                <FormField label="Tenant Phone">
                  <input style={pmStyles.input} value={form.tenantPhone}
                    onChange={(e) => updateForm("tenantPhone", e.target.value)} placeholder="+1 555 000 0000" />
                </FormField>
                <FormField label="Unit ID">
                  <input style={pmStyles.input} value={form.unitId}
                    onChange={(e) => updateForm("unitId", e.target.value)} placeholder="Firestore unit ID" />
                </FormField>
              </div>
              <div style={pmStyles.formRow}>
                <FormField label="Lease Start *">
                  <input style={pmStyles.input} type="date" value={form.startDate}
                    onChange={(e) => updateForm("startDate", e.target.value)} />
                </FormField>
                <FormField label="Lease End *">
                  <input style={pmStyles.input} type="date" value={form.endDate}
                    onChange={(e) => updateForm("endDate", e.target.value)} />
                </FormField>
              </div>
              <div style={pmStyles.formRow}>
                <FormField label="Monthly Rent ($) *">
                  <input style={pmStyles.input} type="number" min="0" value={form.monthlyRentCents}
                    onChange={(e) => updateForm("monthlyRentCents", e.target.value)} placeholder="1500" />
                </FormField>
                <FormField label="Security Deposit ($)">
                  <input style={pmStyles.input} type="number" min="0" value={form.securityDepositCents}
                    onChange={(e) => updateForm("securityDepositCents", e.target.value)} placeholder="1500" />
                </FormField>
              </div>
              <div style={pmStyles.formRow}>
                <FormField label="Late Fee ($)">
                  <input style={pmStyles.input} type="number" min="0" value={form.lateFeeCents}
                    onChange={(e) => updateForm("lateFeeCents", e.target.value)} placeholder="75" />
                </FormField>
                <FormField label="Grace Period (days)">
                  <input style={pmStyles.input} type="number" min="0" value={form.lateFeeGraceDays}
                    onChange={(e) => updateForm("lateFeeGraceDays", e.target.value)} placeholder="5" />
                </FormField>
              </div>
              <FormField label="Pet Policy">
                <input style={pmStyles.input} value={form.petPolicy}
                  onChange={(e) => updateForm("petPolicy", e.target.value)}
                  placeholder="No pets / Cats allowed with $250 deposit / etc." />
              </FormField>
              <FormField label="Special Terms">
                <textarea style={{ ...pmStyles.input, minHeight: 72, resize: "vertical" }}
                  value={form.specialTerms}
                  onChange={(e) => updateForm("specialTerms", e.target.value)}
                  placeholder="Any additional lease terms…" />
              </FormField>

              {formError && <p style={pmStyles.errorMsg}>{formError}</p>}

              <div style={pmStyles.modalFooter}>
                <button style={pmStyles.btnSecondary} onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </button>
                <button
                  style={{ ...pmStyles.btnPrimary, ...(saving ? pmStyles.btnDisabled : {}) }}
                  onClick={handleCreateLease} disabled={saving}
                >
                  {saving ? "Creating…" : "Create Lease Draft"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: brand.colors.text }}>{label}</label>
      {children}
    </div>
  );
}

// ── Public Lease Signing Portal ───────────────────────────────────────────
// Exported separately — used in App.js as /lease?l=LEASE_ID
export function LeaseSigningPortal() {
  const db = getFirestore();
  const params = new URLSearchParams(window.location.search);
  const leaseId = params.get("l");

  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1=review, 2=sign, 3=done

  const [scrolled, setScrolled] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState("");

  useEffect(() => {
    if (!leaseId) { setLoading(false); return; }
    getDoc(doc(db, "leases", leaseId)).then((snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setLease(data);
        if (data.signedAt) setStep(3);
      }
      setLoading(false);
    });
  }, [leaseId, db]);

  async function handleSign() {
    setSignError("");
    if (!agreed) { setSignError("Please check the acknowledgment box."); return; }
    if (!signatureName.trim()) { setSignError("Please type your full name as your signature."); return; }
    setSigning(true);
    try {
      await updateDoc(doc(db, "leases", leaseId), {
        signedAt:   serverTimestamp(),
        signedName: signatureName.trim(),
        status:     "active",
        updatedAt:  serverTimestamp(),
      });
      setStep(3);
    } catch {
      setSignError("Signature failed. Please try again.");
    } finally {
      setSigning(false);
    }
  }

  const fmtMoney = (cents) => cents
    ? (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";
  const fmtDate = (s) => s
    ? new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  if (loading) return (
    <div style={portalStyles.page}>
      <div style={portalStyles.card}>
        <div style={portalStyles.loadingWrap}><div style={portalStyles.spinner} /></div>
      </div>
    </div>
  );

  if (!leaseId || !lease) return (
    <div style={portalStyles.page}>
      <div style={portalStyles.card}>
        <div style={portalStyles.errorPage}>
          <span style={{ fontSize: 36 }}>🔍</span>
          <h2 style={portalStyles.errorTitle}>Lease not found</h2>
          <p style={portalStyles.errorSub}>This link may be invalid. Contact your property manager.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={portalStyles.page}>
      <div style={portalStyles.header}>
        <span style={portalStyles.wordmarkCyan}>vertical</span>
        <span style={portalStyles.wordmarkWhite}>stack</span>
        <span style={portalStyles.headerSub}>Lease Agreement</span>
      </div>

      <div style={portalStyles.card}>
        {/* Step indicator */}
        <div style={portalStyles.stepRow}>
          {["Review Lease", "Sign", "Complete"].map((label, i) => (
            <React.Fragment key={i}>
              <div style={portalStyles.stepItem}>
                <div style={{
                  ...portalStyles.stepDot,
                  ...(i + 1 < step ? portalStyles.stepDone : {}),
                  ...(i + 1 === step ? portalStyles.stepActive : {}),
                }}>
                  {i + 1 < step ? "✓" : i + 1}
                </div>
                <span style={{
                  ...portalStyles.stepLabel,
                  ...(i + 1 === step ? portalStyles.stepLabelActive : {}),
                }}>{label}</span>
              </div>
              {i < 2 && (
                <div style={{
                  ...portalStyles.stepLine,
                  ...(i + 1 < step ? portalStyles.stepLineDone : {}),
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 — Review */}
        {step === 1 && (
          <div style={portalStyles.body}>
            <h2 style={portalStyles.stepTitle}>Lease Summary</h2>
            <p style={portalStyles.stepSub}>Review your lease terms before signing.</p>
            <div style={portalStyles.summaryGrid}>
              {[
                ["Tenant", lease.tenantName],
                ["Lease Start", fmtDate(lease.startDate)],
                ["Lease End", fmtDate(lease.endDate)],
                ["Monthly Rent", fmtMoney(lease.monthlyRentCents)],
                ["Security Deposit", fmtMoney(lease.securityDepositCents)],
                ["Late Fee", fmtMoney(lease.lateFeeCents)],
                ["Grace Period", lease.lateFeeGraceDays ? `${lease.lateFeeGraceDays} days` : "—"],
                ...(lease.petPolicy ? [["Pet Policy", lease.petPolicy]] : []),
                ...(lease.specialTerms ? [["Special Terms", lease.specialTerms]] : []),
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <span style={portalStyles.summaryLabel}>{label}</span>
                  <span style={portalStyles.summaryValue}>{value || "—"}</span>
                </React.Fragment>
              ))}
            </div>
            <button style={portalStyles.btnPrimary} onClick={() => setStep(2)}>
              Proceed to Sign →
            </button>
          </div>
        )}

        {/* Step 2 — Sign */}
        {step === 2 && (
          <div style={portalStyles.body}>
            <h2 style={portalStyles.stepTitle}>Sign Your Lease</h2>
            <p style={portalStyles.stepSub}>
              Read the lease agreement below, then sign with your typed name.
            </p>

            <div
              style={portalStyles.leaseBox}
              onScroll={(e) => {
                const el = e.target;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolled(true);
              }}
            >
              <p style={portalStyles.leasePara}><strong>RESIDENTIAL LEASE AGREEMENT</strong></p>
              <p style={portalStyles.leasePara}>
                This Lease Agreement is entered into between the Property Manager (Landlord) and{" "}
                <strong>{lease.tenantName}</strong> (Tenant).
              </p>
              <p style={portalStyles.leasePara}>
                <strong>TERM:</strong> The lease begins on <strong>{fmtDate(lease.startDate)}</strong> and
                ends on <strong>{fmtDate(lease.endDate)}</strong>, unless terminated earlier in accordance
                with the terms of this agreement.
              </p>
              <p style={portalStyles.leasePara}>
                <strong>RENT:</strong> Tenant agrees to pay <strong>{fmtMoney(lease.monthlyRentCents)}</strong> per
                month, due on the 1st of each month. A grace period of {lease.lateFeeGraceDays || 5} days
                applies. Late fees of {fmtMoney(lease.lateFeeCents) || "as specified"} will be applied
                after the grace period.
              </p>
              <p style={portalStyles.leasePara}>
                <strong>SECURITY DEPOSIT:</strong> A security deposit of{" "}
                <strong>{fmtMoney(lease.securityDepositCents)}</strong> is required prior to move-in.
                The deposit will be held in accordance with applicable state law and returned within the
                legally required timeframe after lease termination, less any legitimate deductions.
              </p>
              <p style={portalStyles.leasePara}>
                <strong>OCCUPANCY:</strong> The premises shall be occupied only by the named tenant(s).
                Subletting without written landlord approval is prohibited.
              </p>
              <p style={portalStyles.leasePara}>
                <strong>MAINTENANCE:</strong> Tenant shall keep the premises in clean condition and notify
                Landlord promptly of any needed repairs. Tenant is responsible for damages caused by
                negligence or misuse.
              </p>
              {lease.petPolicy && (
                <p style={portalStyles.leasePara}>
                  <strong>PET POLICY:</strong> {lease.petPolicy}
                </p>
              )}
              {lease.specialTerms && (
                <p style={portalStyles.leasePara}>
                  <strong>SPECIAL TERMS:</strong> {lease.specialTerms}
                </p>
              )}
              <p style={portalStyles.leasePara}>
                <strong>GOVERNING LAW:</strong> This agreement is governed by the laws of the state in
                which the property is located. In the event of any conflict, applicable state law controls.
              </p>
              <p style={portalStyles.leasePara}>
                By signing below, Tenant acknowledges they have read, understood, and agree to be bound
                by all terms of this Lease Agreement. This constitutes a legally binding electronic
                signature under the E-SIGN Act and UETA.
              </p>
              {!scrolled && (
                <div style={portalStyles.scrollHint}>↓ Scroll to read full agreement</div>
              )}
            </div>

            <div style={portalStyles.checkRow}>
              <input type="checkbox" id="lease-ack" checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer" }} />
              <label htmlFor="lease-ack" style={portalStyles.checkLabel}>
                I have read and agree to all terms of this Lease Agreement
              </label>
            </div>

            <label style={portalStyles.label}>Type your full legal name as your signature</label>
            <input style={portalStyles.input} placeholder="Full legal name"
              value={signatureName} onChange={(e) => setSignatureName(e.target.value)} />

            {signError && <p style={portalStyles.errorMsg}>{signError}</p>}

            <button
              style={{
                ...portalStyles.btnPrimary,
                ...(!agreed || !signatureName.trim() ? portalStyles.btnDisabled : {}),
              }}
              onClick={handleSign}
              disabled={signing || !agreed || !signatureName.trim()}
            >
              {signing ? "Signing…" : "Sign Lease →"}
            </button>
          </div>
        )}

        {/* Step 3 — Done */}
        {step === 3 && (
          <div style={portalStyles.body}>
            <div style={portalStyles.successIcon}>✓</div>
            <h2 style={portalStyles.stepTitle}>Lease Signed!</h2>
            <p style={portalStyles.stepSub}>
              Your lease has been signed and is now active. Your property manager will be in touch
              with next steps including security deposit payment and move-in details.
            </p>
            <div style={portalStyles.leaseConfirmBox}>
              <div style={portalStyles.confirmRow}>
                <span style={portalStyles.confirmLabel}>Signed Name</span>
                <span style={portalStyles.confirmValue}>{lease.signedName || signatureName}</span>
              </div>
              <div style={portalStyles.confirmRow}>
                <span style={portalStyles.confirmLabel}>Lease Term</span>
                <span style={portalStyles.confirmValue}>{fmtDate(lease.startDate)} – {fmtDate(lease.endDate)}</span>
              </div>
              <div style={portalStyles.confirmRow}>
                <span style={portalStyles.confirmLabel}>Monthly Rent</span>
                <span style={portalStyles.confirmValue}>{fmtMoney(lease.monthlyRentCents)}</span>
              </div>
            </div>
            <p style={portalStyles.helpNote}>Keep this page for your records. Contact your property manager with any questions.</p>
          </div>
        )}
      </div>
      <div style={portalStyles.footer}>Powered by <strong>verticalstack</strong> · stack more, manage less</div>
    </div>
  );
}

// ── PM Styles ─────────────────────────────────────────────────────────────
const pmStyles = {
  wrapper: { display: "flex", flexDirection: "column" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "24px 24px 0" },
  heading: { fontFamily: "Arial, sans-serif", fontSize: 20, fontWeight: 700, color: brand.colors.navy, margin: 0 },
  subheading: { fontSize: 13, color: brand.colors.textMuted, margin: "4px 0 0" },
  list: { display: "flex", flexDirection: "column", margin: "16px 24px", border: `1px solid ${brand.colors.border}`, borderRadius: 10, overflow: "hidden" },
  leaseRow: {
    display: "flex", alignItems: "center", padding: "14px 16px",
    gap: 12, background: brand.colors.cardBg, cursor: "pointer",
    borderTop: `1px solid ${brand.colors.border}`, transition: "background 0.1s",
  },
  leaseMain: { flex: 1 },
  leaseTenant: { fontSize: 14, fontWeight: 600, color: brand.colors.text, fontFamily: "Arial, sans-serif" },
  leaseMeta: { fontSize: 12, color: brand.colors.textMuted, marginTop: 2 },
  copyBtn: {
    padding: "5px 12px", background: "white", color: brand.colors.navy,
    border: `1px solid ${brand.colors.border}`, borderRadius: 6,
    fontSize: 11, fontWeight: 600, cursor: "pointer",
  },
  arrowLink: { fontSize: 14, color: brand.colors.textMuted },
  infoRow: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${brand.colors.border}`, gap: 12 },
  infoLabel: { fontSize: 12, fontWeight: 600, color: brand.colors.textMuted, flexShrink: 0 },
  infoValue: { fontSize: 13, color: brand.colors.text, textAlign: "right", wordBreak: "break-all" },
  signingLinkBox: { padding: "12px", background: brand.colors.pageBg, borderRadius: 8, border: `1px solid ${brand.colors.border}`, marginTop: 8 },
  signingLinkLabel: { fontSize: 11, fontWeight: 700, color: brand.colors.textMuted, display: "block", marginBottom: 6 },
  signingLinkRow: { display: "flex", gap: 8, alignItems: "center" },
  signingLink: { fontSize: 11, color: brand.colors.navy, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  btnNew: { padding: "10px 18px", background: brand.colors.navy, color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif", flexShrink: 0 },
  btnSmallPrimary: { padding: "5px 12px", background: brand.colors.navy, color: "white", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0 },
  btnPrimary: { padding: "10px 20px", background: brand.colors.navy, color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" },
  btnSecondary: { padding: "10px 20px", background: "white", color: brand.colors.text, border: `1px solid ${brand.colors.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" },
  btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 32px", margin: "0 24px", border: `1px solid ${brand.colors.border}`, borderRadius: 10, background: brand.colors.cardBg },
  emptyTitle: { fontSize: 16, fontWeight: 700, color: brand.colors.text, margin: "0 0 6px", fontFamily: "Arial, sans-serif" },
  emptySub: { fontSize: 13, color: brand.colors.textMuted, margin: "0 0 20px", textAlign: "center" },
  loadingWrap: { display: "flex", alignItems: "center", justifyContent: "center", padding: 48 },
  spinner: { width: 20, height: 20, border: `2px solid ${brand.colors.border}`, borderTopColor: brand.colors.cyan, borderRadius: "50%", animation: "spin 0.7s linear infinite" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modal: { background: brand.colors.cardBg, borderRadius: 14, width: "100%", maxWidth: 600, boxShadow: "0 20px 60px rgba(0,48,96,0.18)", overflow: "hidden", maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: `1px solid ${brand.colors.border}`, position: "sticky", top: 0, background: brand.colors.cardBg, zIndex: 1 },
  modalTitle: { fontFamily: "Arial, sans-serif", fontSize: 16, fontWeight: 700, color: brand.colors.navy, margin: 0 },
  closeBtn: { background: "none", border: "none", fontSize: 16, color: brand.colors.textMuted, cursor: "pointer" },
  modalBody: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  modalFooter: { display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 },
  formRow: { display: "flex", gap: 12 },
  input: { padding: "10px 12px", border: `1px solid ${brand.colors.border}`, borderRadius: 8, fontSize: 13, color: brand.colors.text, background: brand.colors.pageBg, outline: "none", fontFamily: "Arial, sans-serif", width: "100%", boxSizing: "border-box" },
  errorMsg: { fontSize: 12, color: "#dc2626", padding: "8px 12px", background: "#fef2f2", borderRadius: 6, borderLeft: "3px solid #dc2626", margin: 0 },
};

// ── Portal Styles ─────────────────────────────────────────────────────────
const portalStyles = {
  page: { minHeight: "100vh", background: "#0D1B2A", display: "flex", flexDirection: "column", alignItems: "center", padding: "0 16px 40px" },
  header: { padding: "24px 0 20px", display: "flex", alignItems: "center", gap: 6 },
  wordmarkCyan: { fontFamily: "Arial, sans-serif", fontWeight: 800, fontSize: 22, color: brand.colors.cyan },
  wordmarkWhite: { fontFamily: "Arial, sans-serif", fontWeight: 800, fontSize: 22, color: "white" },
  headerSub: { fontSize: 12, color: "rgba(255,255,255,0.45)", marginLeft: 8, borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 8 },
  card: { background: "white", borderRadius: 16, width: "100%", maxWidth: 560, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" },
  stepRow: { display: "flex", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${brand.colors.border}` },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 },
  stepDot: { width: 24, height: 24, borderRadius: "50%", background: brand.colors.pageBg, color: brand.colors.textMuted, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${brand.colors.border}` },
  stepActive: { background: brand.colors.navy, color: "white", border: `1px solid ${brand.colors.navy}` },
  stepDone: { background: brand.colors.cyan, color: "white", border: `1px solid ${brand.colors.cyan}` },
  stepLine: { flex: 1, height: 2, background: brand.colors.border, margin: "0 6px", marginBottom: 16 },
  stepLineDone: { background: brand.colors.cyan },
  stepLabel: { fontSize: 10, color: brand.colors.textMuted, fontWeight: 500, whiteSpace: "nowrap" },
  stepLabelActive: { color: brand.colors.navy, fontWeight: 700 },
  body: { padding: "24px 24px 28px", display: "flex", flexDirection: "column", gap: 14 },
  stepTitle: { fontFamily: "Arial, sans-serif", fontSize: 18, fontWeight: 700, color: brand.colors.navy, margin: 0 },
  stepSub: { fontSize: 14, color: brand.colors.textMuted, margin: 0, lineHeight: 1.6 },
  summaryGrid: { display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", padding: "12px 16px", background: brand.colors.pageBg, borderRadius: 10, border: `1px solid ${brand.colors.border}` },
  summaryLabel: { fontSize: 12, fontWeight: 600, color: brand.colors.textMuted },
  summaryValue: { fontSize: 13, color: brand.colors.text, textAlign: "right" },
  leaseBox: { height: 260, overflowY: "auto", border: `1px solid ${brand.colors.border}`, borderRadius: 8, padding: "14px 16px", background: brand.colors.pageBg, position: "relative" },
  leasePara: { fontSize: 12, color: brand.colors.text, lineHeight: 1.7, margin: "0 0 12px" },
  scrollHint: { position: "sticky", bottom: 0, textAlign: "center", fontSize: 11, color: brand.colors.textMuted, padding: "4px 0", background: "linear-gradient(transparent, #f8f9fa)" },
  checkRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  checkLabel: { fontSize: 13, color: brand.colors.text, lineHeight: 1.5, cursor: "pointer" },
  label: { fontSize: 13, fontWeight: 600, color: brand.colors.text },
  input: { padding: "12px 14px", border: `1px solid ${brand.colors.border}`, borderRadius: 8, fontSize: 14, color: brand.colors.text, background: brand.colors.pageBg, outline: "none", fontFamily: "Arial, sans-serif" },
  btnPrimary: { padding: "13px 0", background: brand.colors.navy, color: "white", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  errorMsg: { fontSize: 13, color: "#dc2626", padding: "8px 12px", background: "#fef2f2", borderRadius: 6, borderLeft: "3px solid #dc2626", margin: 0 },
  successIcon: { width: 52, height: 52, borderRadius: "50%", background: brand.colors.cyan, color: "white", fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" },
  leaseConfirmBox: { padding: "14px 16px", background: brand.colors.pageBg, borderRadius: 10, border: `1px solid ${brand.colors.border}` },
  confirmRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${brand.colors.border}` },
  confirmLabel: { fontSize: 12, fontWeight: 600, color: brand.colors.textMuted },
  confirmValue: { fontSize: 13, color: brand.colors.text },
  helpNote: { fontSize: 13, color: brand.colors.textMuted, textAlign: "center", margin: 0 },
  loadingWrap: { display: "flex", alignItems: "center", justifyContent: "center", padding: 48 },
  spinner: { width: 24, height: 24, border: `2px solid ${brand.colors.border}`, borderTopColor: brand.colors.cyan, borderRadius: "50%", animation: "spin 0.7s linear infinite" },
  errorPage: { display: "flex", flexDirection: "column", alignItems: "center", padding: 48, gap: 12 },
  errorTitle: { fontFamily: "Arial, sans-serif", fontSize: 20, fontWeight: 700, color: brand.colors.navy, margin: 0 },
  errorSub: { fontSize: 14, color: brand.colors.textMuted, textAlign: "center", margin: 0 },
  footer: { marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.3)" },
};
